import { useId, type FormEvent } from "react";
import { useClients } from "@/api/clients";
import { useVessels, type VesselCall, type VesselCallInput } from "@/api/vessels";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextField, type SelectOption } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type VesselCallFormProps = {
  initial?: VesselCall | null;
  submitLabel?: string;
  onSubmit: (input: VesselCallInput) => Promise<unknown>;
  onCancel?: () => void;
  onReload?: () => void;
};

export function VesselCallForm({
  initial,
  submitLabel = "Save vessel call",
  onSubmit,
  onCancel,
  onReload,
}: VesselCallFormProps) {
  const id = useId();
  const clients = useClients("", false);
  const vessels = useVessels("", false);
  const { values, set } = useFormValues<VesselCallInput>({
    vessel_id: initial?.vessel_id ?? "",
    client_id: initial?.client_id ?? "",
    voyage: initial?.voyage ?? "",
    terminal: initial?.terminal ?? "",
    port: initial?.port ?? "",
    arrival: initial?.arrival ?? "",
    berth: initial?.berth ?? "",  // "" while typing; sent as null when left empty
    departure: initial?.departure ?? "",
    service_from: initial?.service_from ?? "",
    service_to: initial?.service_to ?? "",
  });
  const { error, errors, saving, run } = useSubmitState();

  const clientOptions: SelectOption[] = (clients.data ?? []).map((client) => ({
    value: client.id,
    label: `${client.code} - ${client.legal_name}`,
  }));
  if (initial && !clientOptions.some((option) => option.value === initial.client_id)) {
    clientOptions.push({ value: initial.client_id, label: initial.client_code });
  }
  const vesselOptions: SelectOption[] = (vessels.data ?? []).map((vessel) => ({
    value: vessel.id,
    label: `${vessel.name} (IMO ${vessel.imo})`,
  }));
  if (initial && !vesselOptions.some((option) => option.value === initial.vessel_id)) {
    vesselOptions.push({
      value: initial.vessel_id,
      label: `${initial.vessel_name} (IMO ${initial.vessel_imo})`,
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        ...values,
        voyage: values.voyage.trim(),
        terminal: values.terminal.trim(),
        port: values.port.trim(),
        // An empty date means the client never told us, not the first of January.
        berth: values.berth || null,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <SelectField
          id={`${id}-client`}
          label="Client"
          value={values.client_id}
          onChange={(value) => set("client_id", value)}
          options={clientOptions}
          placeholder="Choose a client"
          error={errors.client_id}
        />
        <SelectField
          id={`${id}-vessel`}
          label="Vessel"
          value={values.vessel_id}
          onChange={(value) => set("vessel_id", value)}
          options={vesselOptions}
          placeholder="Choose a vessel"
          error={errors.vessel_id}
          hint="Add the vessel first if it is not listed"
        />
        <TextField
          id={`${id}-terminal`}
          label="Terminal"
          value={values.terminal}
          onChange={(value) => set("terminal", value)}
          error={errors.terminal}
          hint="e.g. Red Sea Gateway Terminal (RSGT)"
        />
        <TextField
          id={`${id}-port`}
          label="Port"
          value={values.port}
          onChange={(value) => set("port", value)}
          error={errors.port}
          hint="e.g. Jeddah Islamic Port, KSA (SARGT)"
        />
        <TextField
          id={`${id}-voyage`}
          label="Voyage"
          value={values.voyage}
          onChange={(value) => set("voyage", value)}
          error={errors.voyage}
          hint="Optional"
        />
        <div className="hidden md:block" aria-hidden />
        <TextField
          id={`${id}-arrival`}
          label="Arrival"
          type="date"
          value={values.arrival}
          onChange={(value) => set("arrival", value)}
          error={errors.arrival}
        />
        <TextField
          id={`${id}-berth`}
          label="Berthed"
          type="date"
          value={values.berth ?? ""}
          onChange={(value) => set("berth", value)}
          error={errors.berth}
          hint="Optional; prints on the invoice between arrival and departure"
        />
        <TextField
          id={`${id}-departure`}
          label="Departure"
          type="date"
          value={values.departure}
          onChange={(value) => set("departure", value)}
          error={errors.departure}
        />
        <TextField
          id={`${id}-service-from`}
          label="Planning started"
          type="date"
          value={values.service_from}
          onChange={(value) => set("service_from", value)}
          error={errors.service_from}
        />
        <TextField
          id={`${id}-service-to`}
          label="Planning ended"
          type="date"
          value={values.service_to}
          onChange={(value) => set("service_to", value)}
          error={errors.service_to}
        />
      </div>
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
